import environment from '../../../common/environment';
import { PerformSearchParams, SearchRequest, ViewType, ViewTypeLabel } from '../interfaces/search.interface';
import { postSearchClickthrough } from '../api/search-clickthrough.api';
import { searchParam, selectRequest } from '../store/search.selector';
import { mapCatalog, SortByValueMapping } from '../../../common/utils/util';
import { SearchResultPage } from '../../search-v3/search-v3.interface';
import { processSearchTerms } from '../../../common/utils/search-util';
import { setCardView, setListView } from '../store/search.store';
import { mhecommLocalStorage } from '../../../common/local-storage';
import { SearchTermAnalyticsConstants } from '../../../common/constants/anayltics.constants';
import { selectCurrentPageIndex, selectCurrentPageLength } from '../store/search.selector';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { EXPIRATION_TIME_IN_MINS } from '../../../common/events';
import { SearchLocalStorageInterface, SearchLocalStorageInterfaceParam } from '../interfaces/search-local-storage.interface';
import { PdpLocalStorageInterface, PdpLocalStorageInterfaceParam } from '../interfaces/pdp-local-storage.interface';
import { CartLocalStorageInterface, CartLocalStorageInterfaceParam, ReadCartLSObjectFromPdpLSInterface, ReadCartLSObjectFromSearchLSInterface } from '../interfaces/cart-local-storage.interface';

/**
 * Builds and returns the search body based on the parameters.
 *
 * @param params
 */
export function getSearchBody(params: PerformSearchParams): SearchRequest {
  const body: SearchRequest = {
    hits: true,
    facets: params.facets ?? true,
    searchTerms: processSearchTerms(params.searchTerms),
    start: params.start || 0,
    pageLength: params.pageLength || 10,
    catalog: params.catalog || 'UNIFIED',
    excludeFacetName: params.excludeFacetName,
    categoryId: params.categoryId,
    isbnList: params.isbnList,
    programIdList: params.programIdList,
    requireProgramData: params.requireProgramData,
    forceIsbnSearch: params.forceIsbnSearch
  };

  if (params.isPlpSearch) {
    body.isPlpSearch = params.isPlpSearch;
  }

  if (params.fieldValueFilter && Object.keys(params.fieldValueFilter).length > 0) {
    body.fieldValueFilter = params.fieldValueFilter;
  }

  if (params.filter) {
    body.filter = params.filter;
  }

  if (params.sortBy) {
    body.sorts = SortByValueMapping[params.sortBy];
  }

  return body;
}

/**
 * Returns the search URL based on the selected catalog.
 *
 * @param catalog
 * @param store
 * @param categoryId
 */
export function getSearchUrl(catalog = 'UNIFIED', store?: string, categoryId?: string) {
  if (catalog === 'SEG_STORE' && store) {
    return `${environment().searchServiceUrl}/custom-store/${store}/browse`;
  } else if (catalog === 'HE' && categoryId) {
    return `${environment().searchServiceUrl}/browse`;
  } else {
    return `${environment().searchServiceUrl}/search`;
  }
}

/**
 * Toggles the search list or card view in Redux.
 *
 * @param view
 */
export const toggleView = (view: ViewType = ViewTypeLabel.LIST) => (dispatch: Function) => {
  dispatch(view === ViewTypeLabel.LIST ? setListView() : setCardView());
};

/**
 * Generates a search URL with the provided query parameters.
 * 
 * @param {Object} params - The parameters for generating the search URL.
 * @param {string} [params.searchQuery=''] - The search query string.
 * @param {string} [params.page='1'] - The page number for pagination.
 * @param {string} [params.sortby='relevance'] - The sorting criterion (e.g., relevance, date).
 * @param {string} [params.order='desc'] - The sort order (e.g., ascending or descending).
 * @param {string} [params.businessUnit='all'] - The business unit filter (e.g., all, unified, specific units).
 * @param {string} [params.discipline=''] - The discipline filter (optional).
 * @param {string} [params.searchResultPage='default'] - The search result page type ('default' or 'support').
 * @param {string} [params.product=''] - The product filter (for support search).
 * @param {string} [params.learningLevel=''] - The learning level filter (for support search).
 * @param {string} [params.userType=''] - The user type filter (for support search).
 * @returns {string} - The generated search URL as a string.
 *
 * The function constructs a URL using the base search URL from the environment configuration.
 * It normalizes the `businessUnit` parameter, treating 'unified' as 'all'.
 * Query parameters such as `searchQuery`, `page`, `sortby`, `order`, and `businessUnit` are always included.
 * If a discipline is provided, it is added to the query as `disciplines`.
 * If searchResultPage is 'support', it uses supportSearchUrl and adds support-specific parameters.
 */
export const generateSearchUrl = ({
  searchQuery = '',
  page = '1',
  sortby = 'relevance',
  order = 'desc',
  businessUnit = 'all',
  discipline = '',
  searchResultPage = SearchResultPage.DEFAULT,
  product = '',
  learningLevel = '',
  userType = '',
  createdByDivision = '',
}: {
  searchQuery?: string;
  page?: string;
  sortby?: string;
  order?: string;
  businessUnit?: string;
  discipline?: string;
  searchResultPage?: SearchResultPage;
  product?: string;
  learningLevel?: string;
  userType?: string;
  createdByDivision?: string;
}) => {
  // Support or default will use the same search result interface
  const baseUrl = environment().searchUrl;
    
  const url = new URL(baseUrl);
  
  businessUnit = businessUnit.toLowerCase() == 'unified' ? 'all' : businessUnit;
  url.searchParams.set('searchQuery', searchQuery);
  url.searchParams.set('page', page);
  url.searchParams.set('sortby', sortby);
  url.searchParams.set('order', order);
  url.searchParams.set('bu', businessUnit.toLocaleLowerCase());

  if (discipline) {
    url.searchParams.set('disciplines', discipline);
  }
  
  // Only add support-specific parameters if it's a support search
  if (searchResultPage === SearchResultPage.SUPPORT) {
    url.searchParams.set('tab', 'Support');
    
    if (product && product !== 'all') {
      url.searchParams.set('product', product);
    }

    if (learningLevel && learningLevel !== 'all') {
      url.searchParams.set('learning_level', learningLevel);
    }

    if (userType && userType !== 'all') {
      url.searchParams.set('user_type', userType);
    }

    if (createdByDivision && createdByDivision !== 'all') {
      url.searchParams.set('created_by_division', createdByDivision);
    }

    // Add referer tracking (from search-v2)
    url.searchParams.set('referer', window.location.origin + window.location.pathname);
    url.searchParams.set('refererLabel', document.title.split("|")[0].trim());
  }

  return url.toString();
};

export const saveSearchLocalStorageObject = (
  searchLocalStorageInterfaceParam: SearchLocalStorageInterfaceParam,
) => {
  try {
    // check if the RESET_SEARCH_LOCAL_STORAGE is set to true, reset the SEARCH_PAGE_LOCAL_STORAGE_OBJECT and the RESET_SEARCH_LOCAL_STORAGE to false
    const storedValue = localStorage.getItem(SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE);
    const isResetSearchLS = storedValue !== null && JSON.parse(storedValue) === true;
    if (isResetSearchLS) {
      initializeSearchLocalStorageObject(
        searchLocalStorageInterfaceParam.searchTerm,
        searchLocalStorageInterfaceParam.totalResults
      );
      localStorage.setItem(SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE, 'false');
      return;
    }

    const searchLocalStorageValue = getSearchPageLocalStorageObject();

    // if value is present && RESET_SEARCH_LOCAL_STORAGE is set to false, increase the attempt count and add to terms array
    if (searchLocalStorageValue) {
      mhecommLocalStorage.set(
        SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT,
        populateValueForSearchLocalStorageObject({
          attempt: searchLocalStorageValue?.searchAttempts || 0, 
          terms: searchLocalStorageValue?.searchTerms || [], 
          searchTerm: searchLocalStorageInterfaceParam.searchTerm, 
          totalSearchResults: searchLocalStorageInterfaceParam.totalResults
        }),
        null
      );
      return;
    }

    // if value not found for SEARCH_PAGE_LOCAL_STORAGE_OBJECT and also the RESET_SEARCH_LOCAL_STORAGE is false (probably on calling the search the very 1st time)
    initializeSearchLocalStorageObject(
      searchLocalStorageInterfaceParam.searchTerm,
      searchLocalStorageInterfaceParam.totalResults
    );
  } catch (error) {
    console.error('Error in saveSearchLocalStorageObject.');
  }
}

function populateValueForSearchLocalStorageObject(
  searchLocalStorageInterface: SearchLocalStorageInterface,
) {
  return {
    searchAttempts: searchLocalStorageInterface.attempt + 1,
    searchTerms: [
      ...searchLocalStorageInterface.terms,
      { 
        searchTerm: searchLocalStorageInterface.searchTerm, 
        totalSearchResults: searchLocalStorageInterface.totalSearchResults
      },
    ],
  };
}

const initializeSearchLocalStorageObject = (
  searchTerm: string,
  totalSearchResults: number,
) => {
  try {
    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT,
      { searchAttempts: 1, searchTerms: [{ searchTerm, totalSearchResults }] },
      null
    );
  } catch (error) {
    console.error('Error in initializeSearchLocalStorageObject.');
  }
}

export const emptySearchLocalStorageObject = () => {
  try {
    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT,
      { searchAttempts: 0, searchTerms: [] },
      null
    );
  } catch (error) {
    console.error('Error in emptySearchLocalStorageObject.');
  }
}

export const savePdpLocalStorageObject = (
  pdpLocalStorageInterfaceParam: PdpLocalStorageInterfaceParam,
) => {
  try {
    const pdpLocalStorageValue = getPdpPageLocalStorageObject();
    const searchLocalStorageValue = getSearchPageLocalStorageObject();
    let pdpLocalStorgeObject: any;
  
    // 1st task: setting the value of the object which is to be pushed to the pdp local storage
    if (searchLocalStorageValue) {
      // here there are 2 possible scenarios:
      // 1. when the PDP is loaded from the search page (or some other link) after user has already used the search component at least once,
      // 2. user enters an isbn in the search after searching for other search terms at least once.
      const termsArray = Array.isArray(searchLocalStorageValue.searchTerms) && (searchLocalStorageValue.searchTerms as any[])?.some(term => term?.searchTerm !== null) 
        ? searchLocalStorageValue.searchTerms
        : [];
      pdpLocalStorgeObject = populateValueForPDPLocalStorageObject({
        searchAttempts: pdpLocalStorageInterfaceParam.searchComponentQuery 
          ? (searchLocalStorageValue?.searchAttempts || 0) + 1 
          : (searchLocalStorageValue?.searchAttempts || 1),
        searchTerms: pdpLocalStorageInterfaceParam.searchComponentQuery 
          ? [...termsArray, { searchTerm: pdpLocalStorageInterfaceParam.searchTerm, totalSearchResults: 1}] 
          : (searchLocalStorageValue?.searchTerms || []),
        pageOffset: pdpLocalStorageInterfaceParam.searchComponentQuery 
          ? 1 
          : pdpLocalStorageInterfaceParam.pageOffset || 1,
        index: pdpLocalStorageInterfaceParam.searchComponentQuery 
          ? 1 
          : pdpLocalStorageInterfaceParam.index || 1,
        currentPageLength: pdpLocalStorageInterfaceParam.currentPageLength || selectCurrentPageLength(store.getState() as StoreState) || 1,
      });
      
    } else {
      // set the searchTerm which is generally an isbn as the key and the terms list and attempt as 1
      // check if searchTerm is falsy, then don't store its value
      if (pdpLocalStorageInterfaceParam.searchTerm) {
        pdpLocalStorgeObject = populateValueForPDPLocalStorageObject({
          searchAttempts: 1,
          searchTerms: [{ 
            searchTerm: pdpLocalStorageInterfaceParam.searchTerm, 
            totalSearchResults: 1
          }],
          pageOffset: 1,
          index: 1,
          currentPageLength: pdpLocalStorageInterfaceParam.currentPageLength || selectCurrentPageLength(store.getState() as StoreState) || 1,
        });
      }
    }
  
    // 2nd task: check if the pdp local storage already has values, add another object or else populate only the pdpLocalStorgeObject
    const updatedPdpStorage: Record<string, any> = pdpLocalStorageValue || {};
    updatedPdpStorage[
      pdpLocalStorageInterfaceParam.programSourceId 
        ? pdpLocalStorageInterfaceParam.programSourceId 
        : pdpLocalStorageInterfaceParam.searchTerm!
    ] = pdpLocalStorgeObject;
    
    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT,
      updatedPdpStorage,
      EXPIRATION_TIME_IN_MINS
    );
  
    // 3rd task: reset the value of the search local storage object if its called from search component with the isbn URL to resultant PDP
    if (pdpLocalStorageInterfaceParam.searchComponentQuery) {
      mhecommLocalStorage.set(
        SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT, 
        {
          searchAttempts: 1,
          searchTerms: [{
            searchTerm: pdpLocalStorageInterfaceParam.searchTerm, 
            totalSearchResults: 1
          }]
        },
        null
      );
    }
  
    // 4th task: set the RESET_SEARCH_LOCAL_STORAGE to true on load of PDP, by the search page or searching a specific isbn from the search component
    localStorage.setItem(SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE, 'true');
  } catch (error) {
    console.error('Error in savePdpLocalStorageObject.')
  }
}

function populateValueForPDPLocalStorageObject(
  pdpLocalStorageInterface: PdpLocalStorageInterface,
) {
  return {
    productDiscovery: "search",
    searchAttempts: pdpLocalStorageInterface.searchAttempts,
    searchTerms: pdpLocalStorageInterface.searchTerms,
    pageDetails: {
      pageNumber: pdpLocalStorageInterface.pageOffset,
      index: pdpLocalStorageInterface.index,
      currentPageLength: pdpLocalStorageInterface.currentPageLength,
    }
  };
}

export const saveCartLocalStorageObject = (
  cartLocalStorageInterfaceParam: CartLocalStorageInterfaceParam,
) => {
  try {
    const pdpLocalStorageValue = getPdpPageLocalStorageObject();
    const cartLocalStorageValue = getCartPageLocalStorageObject();
    const searchLocalStorageValue = getSearchPageLocalStorageObject();
    let cartLocalStorageObject;
    // if capturePDPLocalStorage is true, read data from pdpLocalStorage
    if (cartLocalStorageInterfaceParam.capturePDPLocalStorage) {
      cartLocalStorageObject = readCartLSObjectFromPdpLS({
        programSourceId: cartLocalStorageInterfaceParam.programSourceId,
        pdpLocalStorageValue,
        cartLocalStorageValue,
        isbn10: cartLocalStorageInterfaceParam.isbn10,
        isbn13: cartLocalStorageInterfaceParam.isbn13,
      });
    } else {
      // else read from searchLocalStorage and save to cartLocalStorage
      cartLocalStorageObject = readCartLSObjectFromSearchLS({
        searchLocalStorageValue,
        isbn10: cartLocalStorageInterfaceParam.isbn10,
        isbn13: cartLocalStorageInterfaceParam.isbn13,
        index: cartLocalStorageInterfaceParam.index,
      });
    }
  
    // save the object to cart local storage
    const updatedCartStorage: Record<string, any> = cartLocalStorageValue || {};
    if (updatedCartStorage !== cartLocalStorageObject) {
      updatedCartStorage[cartLocalStorageInterfaceParam.programSourceId] = cartLocalStorageObject;
    }

    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT,
      updatedCartStorage,
      EXPIRATION_TIME_IN_MINS
    );
  } catch (error) {
    console.error('Error in saveCartLocalStorageObject.')
  }
}

function readCartLSObjectFromPdpLS(
  readCartLSObjectFromPdpLSInterface: ReadCartLSObjectFromPdpLSInterface,
) {
  try {
    let cartLocalStorageObject;

    // fetch the object from PDP_PAGE_LOCAL_STORAGE_OBJECT matched with the key
    const pdpProgramSourceIdData = readCartLSObjectFromPdpLSInterface.pdpLocalStorageValue 
      ? readCartLSObjectFromPdpLSInterface.pdpLocalStorageValue[readCartLSObjectFromPdpLSInterface.programSourceId]
      : undefined;
    const cartProgramSourceIdData = readCartLSObjectFromPdpLSInterface.cartLocalStorageValue 
      ? readCartLSObjectFromPdpLSInterface.cartLocalStorageValue[readCartLSObjectFromPdpLSInterface.programSourceId]
      : undefined;
  
    // if programSourceIdData is present means the page was loaded from a search component or is already added to cart
    if (pdpProgramSourceIdData || cartProgramSourceIdData) {
      if (pdpProgramSourceIdData) {
        // if present then add to CART_PAGE_LOCAL_STORAGE_OBJECT and remove from PDP_PAGE_LOCAL_STORAGE_OBJECT
        cartLocalStorageObject = populateValueForCartLocalStorageObject({
          productDiscovery: pdpProgramSourceIdData?.productDiscovery,
          searchAttempts: pdpProgramSourceIdData?.searchAttempts,
          searchTerms: pdpProgramSourceIdData?.searchTerms,
          products: [{
            isbn10: readCartLSObjectFromPdpLSInterface.isbn10,
            isbn13: readCartLSObjectFromPdpLSInterface.isbn13
          }],
          pageDetails: pdpProgramSourceIdData?.pageDetails,
        });
  
        delete readCartLSObjectFromPdpLSInterface.pdpLocalStorageValue[readCartLSObjectFromPdpLSInterface.programSourceId];
      } else if (cartProgramSourceIdData) {
        // if the programSourceId is already present in the cart, 2 cases are possible:
        // 1. its an update cart call or 
        // 2. adding different product with same programSourceId
        if (cartProgramSourceIdData?.products) {
          cartProgramSourceIdData.products = addMultipleProdutsToCartObject(
            cartProgramSourceIdData?.products,
            {
              isbn10: readCartLSObjectFromPdpLSInterface.isbn10,
              isbn13: readCartLSObjectFromPdpLSInterface.isbn13,
            }
          );
        }
        readCartLSObjectFromPdpLSInterface.cartLocalStorageValue[readCartLSObjectFromPdpLSInterface.programSourceId] = cartProgramSourceIdData;
        cartLocalStorageObject = readCartLSObjectFromPdpLSInterface.cartLocalStorageValue;
      }
    } else if (fetchParentProgramIdSearchTerms(readCartLSObjectFromPdpLSInterface.programSourceId)) {
      /* check if the programSourceId is present in the RELATED_PRODUCTS_MAPPER
        if present, capture its programId value and fetch that value from the PDP local storage
      */
      const pdpLocalStorageData = fetchParentProgramIdSearchTerms(readCartLSObjectFromPdpLSInterface.programSourceId);
      cartLocalStorageObject = populateValueForCartLocalStorageObject({
        productDiscovery: pdpLocalStorageData?.productDiscovery,
        searchAttempts: pdpLocalStorageData?.searchAttempts,
        searchTerms: pdpLocalStorageData?.searchTerms,
        products: [{
          isbn10: readCartLSObjectFromPdpLSInterface.isbn10,
          isbn13: readCartLSObjectFromPdpLSInterface.isbn13
        }],
        pageDetails: pdpLocalStorageData?.pageDetails,
      });
    } else {
      // if no value is present then save as pd=browse and searchTerm=extsearch
      cartLocalStorageObject = populateValueForCartLocalStorageObject({
        productDiscovery: 'browse',
        searchAttempts: 1,
        searchTerms: ['extsearch'],
        products: [{
          isbn10: readCartLSObjectFromPdpLSInterface.isbn10,
          isbn13: readCartLSObjectFromPdpLSInterface.isbn13,
        }],
        pageDetails: {
          pageNumber: 'extsearch',
          index: 'extsearch',
          currentPageLength: 'extsearch',
        }
      });
    }
  
    // saving the updated value for PDP_PAGE_LOCAL_STORAGE_OBJECT if an entry was deleted
    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT,
      readCartLSObjectFromPdpLSInterface.pdpLocalStorageValue,
      EXPIRATION_TIME_IN_MINS
    );
    return cartLocalStorageObject;
  } catch (error) {
    console.error('Error in readCartLSObjectFromPdpLS.');
  }
}

function addMultipleProdutsToCartObject(products, newProduct): any {
  try {
    if (products && products?.length > 0) {
      const isbnExists = products.some((p) => p?.isbn10 === newProduct.isbn10 && p?.isbn13 === newProduct.isbn13);
      if (!isbnExists) {
        products.push(newProduct);
      }
      return products;
    }
    return newProduct;
  } catch (error) {
    console.error('Error in addMultipleProdutsToCartObject.')
  }
}

function readCartLSObjectFromSearchLS(
  readCartLSObjectFromSearchLSInterface: ReadCartLSObjectFromSearchLSInterface,
) {
  try {
    // set the RESET_SEARCH_LOCAL_STORAGE to true
    localStorage.setItem(SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE, 'true');

    if (readCartLSObjectFromSearchLSInterface.searchLocalStorageValue) {
      return populateValueForCartLocalStorageObject({
        productDiscovery: 'search',
        searchAttempts: readCartLSObjectFromSearchLSInterface.searchLocalStorageValue?.searchAttempts || 0,
        searchTerms: readCartLSObjectFromSearchLSInterface.searchLocalStorageValue?.searchTerms || [],
        products: [{
          isbn10: readCartLSObjectFromSearchLSInterface.isbn10, 
          isbn13: readCartLSObjectFromSearchLSInterface.isbn13,
        }],
        pageDetails: {
          pageNumber: selectCurrentPageIndex(store.getState() as StoreState),
          index: readCartLSObjectFromSearchLSInterface?.index || 1,
          currentPageLength: selectCurrentPageLength(store.getState() as StoreState),
        },
      });
    } else {
      return populateValueForCartLocalStorageObject({
        productDiscovery: 'browse',
        searchAttempts :1,
        searchTerms: ['extsearch'],
        products: [{
          isbn10: readCartLSObjectFromSearchLSInterface.isbn10, 
          isbn13: readCartLSObjectFromSearchLSInterface.isbn13,
        }],
        pageDetails: {
          pageNumber: 'extsearch',
          index: 'extsearch',
          currentPageLength: 'extsearch',
        }
      });
    }
  } catch (error) {
    console.error('Error in readCartLSObjectFromSearchLS');
  }
  return;
}

function populateValueForCartLocalStorageObject(
  cartLocalStorageInterface: CartLocalStorageInterface,
) {
  return {
    productDiscovery: cartLocalStorageInterface.productDiscovery,
    searchAttempts: cartLocalStorageInterface.searchAttempts,
    searchTerms: cartLocalStorageInterface.searchTerms,
    products: cartLocalStorageInterface.products,
    pageDetails: cartLocalStorageInterface.pageDetails,
  }
}

export const removeFromCartLocalStorageObject = (
  programSourceId: string,
  isbn10: string,
  isbn13: string,
) => {
  try {
    // check if the programSourceId is present in the CART_PAGE_LOCAL_STORAGE_OBJECT then delete the entry
    const cartLocalStorageValue = getCartPageLocalStorageObject();
    const cartProgramSourceIdData = cartLocalStorageValue ? cartLocalStorageValue[programSourceId] : undefined;
    if (cartProgramSourceIdData) {
      // if the length of products is 1 then delete the entry from cartLocalStorageObject
      if (cartProgramSourceIdData?.products && cartProgramSourceIdData?.products?.length === 1) {
        delete cartLocalStorageValue[programSourceId];
      }
      // else delete only the product object from the products array and save the updated value of products for programSourceId
      if (cartProgramSourceIdData?.products && cartProgramSourceIdData?.products?.length > 1) {
        cartProgramSourceIdData.products = removeProduct(
          cartProgramSourceIdData.products,
          isbn10,
          isbn13,
        );
        cartLocalStorageValue[programSourceId] = cartProgramSourceIdData;
      }
      
      mhecommLocalStorage.set(
        SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT,
        cartLocalStorageValue,
        EXPIRATION_TIME_IN_MINS,
      );
    }
  } catch (error) {
    console.error('Error in removeFromCartLocalStorageObject.');
  }
}

function removeProduct(
  products,
  isbn10: string,
  isbn13: string,
) {
  try {
    return products.filter(
      (p) => !(p?.isbn10 === isbn10 && p?.isbn13 === isbn13)
    );
  } catch (error) {
    console.error('Error in removeProduct.');
  }
}

export const getPdpPageLocalStorageObject = () => {
  try {
    // if expired then clear the entry from the local storage and return undefined
    if (mhecommLocalStorage.checkExpiry(SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT)) {
      mhecommLocalStorage.remove(SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT);
      return undefined;
    }
    return mhecommLocalStorage.get(SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT);
  } catch (error) {
    console.error('Error in getPdpPageLocalStorageObject.');
  }
}

export const getSearchPageLocalStorageObject = () => {
  try {
    // SEARCH_PAGE_LOCAL_STORAGE_OBJECT doesn't have an expiration time, so not checking its expiry
    return mhecommLocalStorage.get(SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT);
  } catch (error) {
    console.error('Error in getSearchPageLocalStorageObject.');
  }
}

export const getRelatedProductsLocalStorageObject = () => {
  try {
    // RELATED_PRODUCTS_MAPPER doesn't have an expiration time, so not checking its expiry
    return mhecommLocalStorage.get(SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER);
  } catch (error) {
    console.error('Error in getRelatedProductsLocalStorageObject.');
  }
}

export const getCartPageLocalStorageObject = () => {
  try {
    // if expired then clear the entry from the local storage and return undefined
    if (mhecommLocalStorage.checkExpiry(SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT)) {
      mhecommLocalStorage.remove(SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT);
      return undefined;
    }
    return mhecommLocalStorage.get(SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT);
  } catch (error) {
    console.error('Error in getCartPageLocalStorageObject.');
  }
}

export const filterCartItemsOnCheckoutStep = (
  cartProgramIdList: any[],
) => {
  const filteredCart: Record<string, any> = {};
  // fetch the cart local storage object
  const cartLocalStorageValue = getCartPageLocalStorageObject();

  // filter object if key matches any entry in cartProgramIdList
  if (cartLocalStorageValue && cartProgramIdList?.length > 0) {
    Object.entries(cartLocalStorageValue).map(([key, _value]) => {
      if (cartProgramIdList.includes(key)) {
        filteredCart[key] = cartLocalStorageValue[key];
      }
    })
  }

  // save to cart local storage
  mhecommLocalStorage.set(
    SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT,
    filteredCart,
    EXPIRATION_TIME_IN_MINS,
  );
}

export function populateRelatedProductsMapper(relatedProducts: string[], programSourceId: string) {
  try {
    let relatedProductsLocalStorageValue = getRelatedProductsLocalStorageObject();

    if (!relatedProductsLocalStorageValue) {
      relatedProductsLocalStorageValue = {};
    }

    relatedProducts?.map((product) => {
      if (!relatedProductsLocalStorageValue[product]) {
        relatedProductsLocalStorageValue[product] = { programId: programSourceId };
      }
    });

    mhecommLocalStorage.set(
      SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER,
      relatedProductsLocalStorageValue,
      null
    )
  } catch (error) {
    console.error('Error in populateRelatedProductsMapperLS.');
  }
}

export function fetchParentProgramIdSearchTerms(programId: string) {
  try {
    const pdpLocalStorageValue = getPdpPageLocalStorageObject();
    const parentProgramSourceId = fetchParentProductProgramId(programId);

    if (parentProgramSourceId && pdpLocalStorageValue) {
      return pdpLocalStorageValue[parentProgramSourceId];
    }
  } catch (error) {
    console.error('Error in fetchParentProgramIdSearchTerms.');
  }
}

export function fetchParentProductProgramId(programId: string) {
  try {
    const relatedProductsLocalStorageValue = getRelatedProductsLocalStorageObject();

    if (relatedProductsLocalStorageValue) {
      return relatedProductsLocalStorageValue[programId]?.programId;
    }
  } catch (error) {
    console.error('Error in fetchParentProductProgramId.');
  }
}

export const deleteRelatedProductMapper = () => {
  try {
    mhecommLocalStorage.remove(SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER);
  } catch (error) {
    console.error('Error in deleteRelatedProductMapper.');
  }
}

export function trackSearchClickthrough(documentId: string, productBusinessUnit?: string) {
  try {
    const state = store.getState() as StoreState;
    const searchTerms = searchParam(state) ?? '';
    const requestCatalog = selectRequest(state)?.catalog;
    const catalog = mapCatalog(requestCatalog ?? '') || mapCatalog(productBusinessUnit ?? '');
    if (!catalog) return;
    void postSearchClickthrough({ searchTerms, catalog, documentId }).catch((error: unknown) => {
      console.error('Failed to record search clickthrough', error instanceof Error ? error.message : String(error));
    });
  } catch (error) {
    console.error('Failed to record search clickthrough', error instanceof Error ? error.message : String(error));
  }
}

export function findElementInContexts<T extends Element>(selector: string, contexts: ParentNode[], selectAll = false): T | NodeListOf<T> | undefined {
  try {
    for (const context of contexts) {
      if (selectAll) {
        const found = context.querySelectorAll(selector);
        if (found.length > 0) {
          return found as NodeListOf<T>;
        }
      } else {
        const found = context.querySelector(selector);
        if (found) {
          return found as T;
        }
      }
    }

    return;
  } catch (error) {
    console.error('Error in findElementInContexts.');
    return;
  }
}
